import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  ExclamationCircleIcon 
} from '@heroicons/react/24/outline';

import { 
  useRelease, 
  useReleaseEditor 
} from '../hooks/useRelease';
import { useReleaseFlows, useReleaseFlow } from '../hooks/useReleaseFlow';
import { useRoles } from '../auth/useRoles';
import { LoadingSpinner } from '../components/common';
import type { 
  CreateReleaseDto, 
  UpdateReleaseDto
} from '../types/release';

export const ReleaseFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite, isLoading: rolesLoading } = useRoles();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    releaseFlowId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch data
  const { data: release, isLoading: releaseLoading } = useRelease(id);
  const { data: releaseFlowsData, isLoading: releaseFlowsLoading } = useReleaseFlows({});
  const { data: selectedReleaseFlow } = useReleaseFlow(formData.releaseFlowId || undefined);
  const { saveRelease, isSaving, error } = useReleaseEditor();

  // Initialize form data
  useEffect(() => {
    if (release && isEdit) {
      setFormData({
        name: release.name,
        description: release.description || '',
        releaseFlowId: release.releaseFlowId,
      });
    }
  }, [release, isEdit]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Release name is required';
    } else if (formData.name.length < 1 || formData.name.length > 200) {
      newErrors.name = 'Release name must be between 1 and 200 characters';
    }

    if (!formData.releaseFlowId) {
      newErrors.releaseFlowId = 'Release flow is required';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description cannot exceed 1000 characters';
    }

    // Check for deleted pipelines in the selected release flow
    if (selectedReleaseFlow && !isEdit) {
      const deletedPipelineNodes = selectedReleaseFlow.nodes
        .filter(node => node.nodePipelineLink?.isDeleted === true);
      if (deletedPipelineNodes.length > 0) {
        const nodeNames = deletedPipelineNodes.map(n => n.name).join(', ');
        newErrors.releaseFlowId = `Cannot create release: the following nodes have pipelines that were removed from Azure DevOps: ${nodeNames}. Please update or reassign these pipelines first.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const currentUser = 'current-user'; // TODO: Get from auth context

      if (isEdit && release) {
        const updateData: UpdateReleaseDto & { id: string } = {
          id: release.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          updatedBy: currentUser,
        };
        await saveRelease(updateData);
        toast.success('Release updated successfully');
      } else {
        const createData: CreateReleaseDto = {
          releaseFlowId: formData.releaseFlowId,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          createdBy: currentUser,
        };
        await saveRelease(createData);
        toast.success('Release created successfully');
      }

      navigate('/releases');
    } catch (error: any) {
      console.error('Error saving release:', error);
      
      // Handle specific error types
      let errorMessage = `Failed to ${isEdit ? 'update' : 'create'} release`;
      
      if (error?.response?.status === 409) {
        // Conflict error (duplicate)
        const errorData = error.response.data;
        if (errorData?.message) {
          errorMessage = errorData.message;
          if (errorData.detail) {
            errorMessage += ` ${errorData.detail}`;
          }
        } else {
          errorMessage = 'This release configuration already exists. Please try again.';
        }
      } else if (error?.response?.status === 400) {
        // Bad request
        const errorData = error.response.data;
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = 'Invalid release data. Please check your inputs.';
        }
      } else if (error?.response?.status >= 500) {
        // Server error
        const errorData = error.response.data;
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = 'Server error. Please try again later.';
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate('/releases');
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isLoading = releaseLoading || releaseFlowsLoading || rolesLoading;
  const releaseFlows = releaseFlowsData?.data || [];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!canWrite) {
    navigate('/releases');
    return null;
  }

  if (isEdit && !release) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Release not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleCancel}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back to Releases
        </button>
        
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Release' : 'Create New Release'}
        </h1>
        <p className="text-gray-600">
          {isEdit 
            ? 'Update release details and configuration' 
            : 'Create a new release from a release flow'
          }
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-600">Failed to save release. Please try again.</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Release Flow Selection - Only shown for new releases */}
        {!isEdit && (
          <div>
            <label htmlFor="releaseFlowId" className="block text-sm font-medium text-gray-700 mb-2">
              Release Flow *
            </label>
            <select
              id="releaseFlowId"
              value={formData.releaseFlowId}
              onChange={(e) => handleInputChange('releaseFlowId', e.target.value)}
              className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                errors.releaseFlowId ? 'ring-red-300 focus:ring-red-500' : ''
              }`}
            >
              <option value="">Select a release flow</option>
              {releaseFlows.map((flow) => (
                <option key={flow.id} value={flow.id} disabled={!flow.isActive}>
                  {flow.name} {!flow.isActive && '(Inactive)'}
                </option>
              ))}
            </select>
            {errors.releaseFlowId && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                {errors.releaseFlowId}
              </p>
            )}
          </div>
        )}

        {/* Release Flow Display - Only shown for edit */}
        {isEdit && release && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Release Flow
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900">
              {release.releaseFlowName || 'Unknown Release Flow'}
            </div>
          </div>
        )}

        {/* Release Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Release Name *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
              errors.name ? 'ring-red-300 focus:ring-red-500' : ''
            }`}
            placeholder="Enter release name"
            maxLength={200}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <ExclamationCircleIcon className="w-4 h-4 mr-1" />
              {errors.name}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            {formData.name.length}/200 characters
          </p>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
              errors.description ? 'ring-red-300 focus:ring-red-500' : ''
            }`}
            placeholder="Enter optional description"
            maxLength={1000}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <ExclamationCircleIcon className="w-4 h-4 mr-1" />
              {errors.description}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            {formData.description.length}/1000 characters
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center"
            disabled={isSaving}
          >
            {isSaving && <LoadingSpinner size="sm" className="mr-2" />}
            {isEdit ? 'Update Release' : 'Create Release'}
          </button>
        </div>
      </form>

      {/* Additional Info for Edit Mode */}
      {isEdit && release && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Release Information</h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Version</dt>
              <dd className="text-sm text-gray-900">{release.version}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Lifecycle State</dt>
              <dd className="text-sm text-gray-900">{release.lifecycleState}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="text-sm text-gray-900">{release.status}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created On</dt>
              <dd className="text-sm text-gray-900">{new Date(release.createdOn).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created By</dt>
              <dd className="text-sm text-gray-900">{release.createdBy}</dd>
            </div>
            {release.updatedOn && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="text-sm text-gray-900">{new Date(release.updatedOn).toLocaleString()}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
};

export default ReleaseFormPage;